import { useCallback } from "react";

import toast from "components/partials/toast/toast";

import {
  useUpdateMergeTags as useUpdateMergeTagsMutation,
  type TouchpointVersionAttributes,
} from "hooks/api/use-touchpoint-versions";
import { mapTouchpointVersionDTO } from "utilities/api-mappers";
import { getErrorMessage } from "utilities";

type UpdateMergeTagsScope = {
  clientId: string;
  campaignId: string;
  touchpointId: string;
};

const UPDATED_TITLE = "Merge tags updated";
const FAILED_TITLE = "Failed to update merge tags";

/**
 * Clones the version server-side into a new draft with its merge tags updated. Resolves with the
 * new draft so the caller can update its own selection/list - this never navigates anywhere on its
 * own. Rethrows after the error toast so the confirmation dialog that drives this knows the attempt
 * failed and stays open rather than closing on a failed run.
 */
export const useUpdateMergeTags = (scope: UpdateMergeTagsScope) => {
  const updateMergeTagsMutation = useUpdateMergeTagsMutation(scope);

  const updateMergeTags = useCallback(
    (versionId: string): Promise<TouchpointVersionAttributes> =>
      updateMergeTagsMutation
        .mutateAsync(versionId)
        .then((dto) => {
          toast.success({ data: { title: UPDATED_TITLE } });
          return mapTouchpointVersionDTO(dto);
        })
        .catch((err) => {
          toast.error({
            data: {
              title: FAILED_TITLE,
              message: getErrorMessage(err?.response?.data),
            },
          });
          throw err;
        }),
    [updateMergeTagsMutation],
  );

  return { updateMergeTags, isUpdating: updateMergeTagsMutation.isPending };
};
